// Modal that handles two flows for "Join team as athlete":
//   1. User has no AthleteRecord yet → collect gender+DOB+weight+experience and
//      call POST /api/athletes/me (creates AR + pending team_request atomically).
//   2. User already has an AR → just file a POST /api/athletes/me/team-requests
//      against the target team.
//
// Reuses AthleteFormFields for the form portion of (1). Errors from the backend's
// composite-UNIQUE block (ATHLETE_IDENTITY_EXISTS) are surfaced as an actionable
// hint to claim the existing record instead.

import { useState } from 'react';
import { useI18n } from '../../i18n';
import { inlineError } from '../../hooks/useApiCall';
import ExistingAthleteNotice, { identityMatchOf, type IdentityMatch } from '../shared/ExistingAthleteNotice';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import { useLoad } from '../../hooks/useLoad';
import ErrorState from '../shared/ErrorState';
import { getMyAthlete, createMyAthlete, requestTeamJoinAsAthlete } from '../../api/client';
import { personName } from '../../utils/personName';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalFooter from '../shared/ModalFooter';
import type { AthleteRecord, Gender } from '../../types';
import AthleteFormFields, { EMPTY_ATHLETE_FORM, type AthleteFormState } from '../AthleteFormFields';
import { richText } from '../../utils/richText';
import Notice from '../shared/Notice';

interface Props {
  teamId: string;
  teamName: string;
  /** Called after a successful request; the modal shows its own toast (#458). */
  onDone: () => void;
  onClose: () => void;
}

export default function AthleteJoinTeamModal({ teamId, teamName, onDone, onClose }: Props) {
  const { t } = useI18n();
  const { showSuccess } = useToast();

  // #457 — 'error': the own athlete record did not load. It was read as
  // "no record", which offered to create a second one. The phase comes from the load.
  const own = useLoad(() => getMyAthlete().then(r => r.data), [], {
    context: 'AthleteJoinTeamModal:my-athlete',
    initial: null as AthleteRecord | null,
  });
  const ar = own.data;
  const phase: 'loading' | 'error' | 'no-ar' | 'has-ar' =
    own.loading ? 'loading' : own.error ? 'error' : ar ? 'has-ar' : 'no-ar';
  const showLoading = useDeferredLoading(phase === 'loading');
  const [form, setForm] = useState<AthleteFormState>(EMPTY_ATHLETE_FORM);
  // #605, #664 — one lock for both sends: the profile with the request, or the request alone.
  const sending = useBusy();
  const [err, setErr] = useState('');
  const [match, setMatch] = useState<IdentityMatch | null>(null);

  async function submitNoAr(e: React.FormEvent) {
    e.preventDefault();
    await sending.run(async () => {
      setErr(''); setMatch(null);
      try {
        await createMyAthlete({
          gender: form.gender as Gender,
          birth_date: form.birth_date,
          current_weight_kg: form.current_weight_kg ? parseFloat(form.current_weight_kg) : undefined,
          experience_bouts: form.experience_bouts ? parseInt(form.experience_bouts) : undefined,
          team_id: teamId,
        });
        showSuccess(t.teams.profileCreatedRequestSent.replace('{team}', teamName));
        onDone();
      } catch (e) {
        // #658 — the record of the person exists: the notice links it for the claim.
        const found = identityMatchOf(e);
        if (found) setMatch(found);
        else setErr(inlineError(e, 'Could not create athlete profile'));
      }
    });
  }

  async function submitHasAr() {
    await sending.run(async () => {
      setErr('');
      try {
        await requestTeamJoinAsAthlete(teamId);
        showSuccess(t.teams.requestSentTo.replace('{team}', teamName));
        onDone();
      } catch (e) {
        setErr(inlineError(e, 'Could not send request'));
      }
    });
  }

  return (
    <ModalBackdrop onClose={onClose} maxWidth="md" ariaLabel={t.teams.joinAsAthlete}>
      <ModalHeader title={t.teams.joinAsAthlete} />

      {showLoading && (
        <p className="text-sm text-gray-500">{t.common.loading}</p>
      )}
      {phase === 'error' && <ErrorState compact onRetry={own.reload} />}
      {/* #552 — every phase has a footer: a Cancel while the record loads. */}
      {(phase === 'loading' || phase === 'error') && <ModalFooter onCancel={onClose} />}

      {phase === 'has-ar' && ar && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {richText(t.joinTeam.youAre, {
              name: <span className="font-semibold">{personName(ar.first_name, ar.last_name)}</span>,
              team: <span className="font-semibold">{teamName}</span>,
            })}
          </p>
          <p className="text-xs text-gray-500">
            {t.joinTeam.coachApproves.replace('{team}', teamName)}
          </p>
          {err && <Notice tone="error" plain>{err}</Notice>}
          <ModalFooter
            onSave={submitHasAr}
            busy={sending.busy}
            saveLabel={t.joinTeam.sendRequest}
            onCancel={onClose}
          />
        </div>
      )}

      {phase === 'no-ar' && (
        <form onSubmit={submitNoAr} className="space-y-3">
          <p className="text-sm text-gray-700">
            {richText(t.joinTeam.createProfile, { team: <span className="font-semibold">{teamName}</span> })}
          </p>
          <AthleteFormFields value={form} onChange={setForm} requireAll disabled={sending.busy} />
          {err && <Notice tone="error" plain>{err}</Notice>}
          {match && <ExistingAthleteNotice match={match} teamId={teamId} canInvite={false} onInvited={() => {}}
            forSelf={{ birthDate: form.birth_date, onClaimed: onDone }} />}
          <ModalFooter
            saveAsSubmit
            busy={sending.busy}
            saveLabel={t.joinTeam.createAndSend}
            onCancel={onClose}
          />
        </form>
      )}
    </ModalBackdrop>
  );
}
